import {Component, Plugins, Expressions} from '@packages/systems/wfdl';
import {EElement, type ExpressionElement} from '@packages/systems/wfdl-ast';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {analyzeElement, ElementAnalysis, mergeElementAnalyses} from './element';
import {QueryContext} from '../../types';
import {buildComponentInstancesGetter} from '@packages/systems/user-components/utils';
import {getSlotDefaultElementsByFieldId} from '@packages/utilities/platform-slots';
import {emptyArray} from '@packages/utilities/fp/utils';

type ComponentInstancesToAnalyzeStack = {
  prevQueryContext: QueryContext;
  instance: ExpressionElement;
}[];

export interface ComponentAnalyzer {
  ingestComponentAnalysis(
    componentType: Qualified<Component.ComponentName>,
    instanceKey: string,
    dataPathSubTrees: ElementAnalysis['dataPathSubTrees'],
    absoluteQueryPaths: ElementAnalysis['absoluteQueryPaths']
  ): void;
  updateComponentStatus(
    componentType: Qualified<Component.ComponentName>,
    nestedComponentInstances: ExpressionElement[]
  ): void;
  shouldAnalyzeComponent(
    componentType: Qualified<Component.ComponentName>
  ): boolean;
  shouldAnalyzeContent(
    componentType: Qualified<Component.ComponentName>
  ): boolean;
}

export function analyzeComponents(
  componentAnalyzer: ComponentAnalyzer,
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  initialQueryContext: QueryContext,
  componentInstances: ExpressionElement[]
): void {
  const stack: ComponentInstancesToAnalyzeStack = [];
  addComponentInstancesToStack(
    stack,
    componentInstances,
    initialQueryContext,
    componentAnalyzer
  );

  const getComponentInstances = buildComponentInstancesGetter(componentMap);

  while (stack.length > 0) {
    const instanceAndPrevQueryContext = stack.pop();
    if (!instanceAndPrevQueryContext) {
      // TS compiler thinks `instanceAndQueryContext` can be undefined because
      // array.pop() on an empty array returns undefined.
      continue;
    }

    const {instance, prevQueryContext} = instanceAndPrevQueryContext;

    const component = componentMap.get(instance.type);
    if (!component) continue;
    const render = Component.getRender(component);
    const rootElement = render ? Expressions.getElement(render) : null;
    // Slot defaults live on the definition's `defaultData`, so a render-only
    // walk misses both the bindings inside them and the components they place.
    const instanceData = Expressions.getElementData(instance);
    const slotDefaultElements = Array.from(
      getSlotDefaultElementsByFieldId(component)
    ).flatMap(([fieldId, elements]) => {
      const override = Expressions.getIn(instanceData, [
        {in: 'Record', at: fieldId},
      ]);
      // Even an empty slot override replaces the default, so presence is enough.
      if (override) return emptyArray;
      return elements;
    });

    if (
      render &&
      rootElement &&
      componentAnalyzer.shouldAnalyzeComponent(instance.type)
    ) {
      const queryContext = prevQueryContext.createContextForComponent(instance);

      if (queryContext === null) {
        // short circuit for infinite loops from cyclic components
        continue;
      }

      if (componentAnalyzer.shouldAnalyzeContent(instance.type)) {
        const {absoluteQueryPaths, dataPathSubTrees} = mergeElementAnalyses([
          analyzeElement(plugins)(queryContext)(rootElement),
          ...slotDefaultElements.map((root) =>
            analyzeElement(plugins)(queryContext)(root)
          ),
        ]);

        componentAnalyzer.ingestComponentAnalysis(
          instance.type,
          queryContext.instanceKey,
          dataPathSubTrees,
          absoluteQueryPaths
        );
      }

      const nestedComponentInstances = getComponentInstances(render).concat(
        ...slotDefaultElements.map((root) =>
          getComponentInstances(EElement(root))
        )
      );
      componentAnalyzer.updateComponentStatus(
        instance.type,
        nestedComponentInstances
      );

      addComponentInstancesToStack(
        stack,
        nestedComponentInstances,
        queryContext,
        componentAnalyzer
      );
    }
  }
}

function addComponentInstancesToStack(
  stack: ComponentInstancesToAnalyzeStack,
  instances: ExpressionElement[],
  prevQueryContext: QueryContext,
  componentAnalyzer: ComponentAnalyzer
) {
  for (const instance of instances) {
    // prevent adding instances to the stack unnecessarily
    if (componentAnalyzer.shouldAnalyzeComponent(instance.type)) {
      stack.push({instance, prevQueryContext});
    }
  }
}
